import React, { useState, useEffect } from 'react';

const presets = [10, 25, 50, 100];

function Checkout() {
  const paystackPublicKey = 'pk_live_db346d567eebce10aec2dfbf1b8d03c511776ad0';
  const [email, setEmail] = useState('');
  const [amount, setAmount] = useState('');
  const [currency, setCurrency] = useState('USD');
  const [error, setError] = useState('');
  const [paystackLoaded, setPaystackLoaded] = useState(false);

  useEffect(() => {
    const script = document.createElement('script');
    script.src = 'https://js.paystack.co/v1/inline.js';
    script.async = true;
    script.onload = () => setPaystackLoaded(true);
    document.body.appendChild(script);
    return () => { document.body.removeChild(script); };
  }, []);

  const handlePaystackDonateClick = () => {
    if (!email || !amount) { setError('Please enter a valid email and amount.'); return; }
    if (!paystackLoaded) { alert('Paystack is not loaded yet. Please try again.'); return; }
    setError('');
    const handler = window.PaystackPop?.setup({
      key: paystackPublicKey,
      email,
      amount: amount * 100,
      currency,
      callback: (response) => {
        window.location.href = `https://your-success-page.com/success?reference=${response.reference}`;
      },
      onClose: () => { alert('Payment window closed.'); },
    });
    if (handler) handler.openIframe();
    else alert('Failed to initialize Paystack. Please try again.');
  };

  return (
    <div>
      {/* Page header */}
      <div className="bg-gray-900 text-white py-20 px-4 text-center">
        <span className="inline-block text-red-400 text-sm font-semibold uppercase tracking-widest mb-4">Support Our Work</span>
        <h1 className="text-4xl md:text-6xl font-black mb-4 leading-tight">Make a Donation</h1>
        <p className="text-gray-400 text-lg max-w-xl mx-auto">
          Your generosity directly funds our programs and helps us reach more lives every day.
        </p>
      </div>

      <section className="py-24 bg-gray-50">
        <div className="max-w-xl mx-auto px-4">
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8">

            <h2 className="text-2xl font-black text-gray-900 mb-1">Complete your donation</h2>
            <p className="text-gray-400 text-sm mb-8">Every contribution, big or small, makes a real difference.</p>

            {/* Quick amount presets */}
            <div className="mb-6">
              <label className="block text-xs font-semibold text-gray-500 uppercase tracking-widest mb-3">Quick Select</label>
              <div className="grid grid-cols-4 gap-2">
                {presets.map((p) => (
                  <button key={p} type="button"
                    onClick={() => setAmount(String(p))}
                    className={`py-2.5 rounded-xl text-sm font-semibold border-2 transition-all duration-150 ${
                      amount === String(p)
                        ? 'border-red-600 bg-red-600 text-white'
                        : 'border-gray-200 text-gray-600 hover:border-red-300 hover:text-red-600'
                    }`}>
                    ${p}
                  </button>
                ))}
              </div>
            </div>

            <div className="space-y-4">
              {/* Amount */}
              <div>
                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-widest mb-2">Amount</label>
                <div className="relative">
                  <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 font-medium">$</span>
                  <input type="number" placeholder="Enter amount" value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                    className="w-full pl-8 pr-4 py-3 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent transition" />
                </div>
              </div>

              {/* Currency */}
              <div>
                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-widest mb-2">Currency</label>
                <select value={currency} onChange={(e) => setCurrency(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent transition bg-white">
                  <option value="USD">USD — US Dollar</option>
                  <option value="KES">KES — Kenyan Shilling</option>
                </select>
              </div>

              {/* Email */}
              <div>
                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-widest mb-2">Email address</label>
                <input type="email" placeholder="your@email.com" value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent transition" />
              </div>

              {error && (
                <p className="text-red-500 text-sm bg-red-50 border border-red-100 rounded-xl px-4 py-3">{error}</p>
              )}

              <button onClick={handlePaystackDonateClick}
                disabled={!email || !amount || !paystackLoaded}
                className="w-full bg-red-600 hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed text-white font-bold py-4 rounded-xl transition-all duration-200 shadow-sm hover:shadow-md mt-2">
                {paystackLoaded ? `Donate ${amount ? `$${amount}` : 'Now'}` : 'Loading payment...'}
              </button>
            </div>

            <div className="mt-6 flex items-center justify-center gap-2 text-gray-400 text-xs">
              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
              </svg>
              Secured by Paystack · Your details are encrypted
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

export default Checkout;
